/**
 * Engine Atlas position-row cell — quiet structural placeholder rendered in
 * the top row of the 3×3 grid per Atlas Day 2 visual-layout commitment E.1.
 *
 * Architectural purpose (scope §3 "Position row — three cells, no labels" +
 * Atlas Day 2 Phase 0 E.1 lock): render the 3×3 geography honestly. Position
 * row exists as an altitude on the map but doesn't have map content because
 * position synthesis lives in the title block above. E.1 renders both facts
 * (position has its own altitude AND position is synthesized above) by
 * placing minimal-height quiet cells in the position row.
 *
 * Style (E.1 lock): minimal height (h-8 ≈ 32px target range 24-40px),
 * subtle dashed border + faint background as quiet structural indicator. No
 * copy, no per-cell label per scope §3 "Position row — three cells, no
 * labels". aria-hidden=true since the cell carries no semantic content for
 * assistive tech (axis labels in left/top margins teach the geography).
 *
 * E.2 alternative (collapse position row entirely) was rejected at Atlas
 * Day 2 Phase 0 conversation because it removed the "position has its own
 * altitude" fact.
 */

import type { JSX } from 'react';

type PositionCellCoordinate =
  | 'position-input'
  | 'position-calculation'
  | 'position-decision';

export interface PositionRowCellProps {
  cellCoordinate: PositionCellCoordinate;
}

export default function PositionRowCell({
  cellCoordinate,
}: PositionRowCellProps): JSX.Element {
  return (
    <div
      data-cell-coordinate={cellCoordinate}
      aria-hidden="true"
      className="h-8 border border-dashed border-border-subtle bg-surface-0/40 rounded-sm"
    />
  );
}
